import { CheckGroup } from '@divlab/divanui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Filtrator from '@Stores/Filtrator';
import styles from './TypesFilter.module.css';
import useNavigation from '@Navigation/useNavigation';

import type { FormEvent } from 'react';
import type { CheckboxItemData } from '@Components/CheckboxList';

const TypesFilter = () => {
  const [hasSelected, setHasSelected] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  const { pathname, replaceUrl } = useNavigation();
  const filtrator = Filtrator.useFiltrator('catalog');
  const parameter = filtrator.parameters.types;
  const values = filtrator.parameterValues.filter((pv) => {
    return pv.parameterId === 'types';
  });

  const checkboxItems = useMemo(() => {
    const listItems = values
      .map((value, index) => ({
        id: index,
        checked: parameter?.default?.includes(value.value[0]),
        label: value.name,
        color: value.meta && value.meta.color,
        data: value,
        disabled: value?.count === 0,
      }))
      .filter((item) => !item.disabled);

    return listItems;
  }, [parameter, values]);

  const timeoutId = useRef<number>();

  const handleSetHasSelected = useCallback(() => {
    setHasSelected(checkboxItems.some((item) => item.checked));
  }, [checkboxItems]);

  // Задержка для установки состояния hasSelected т.к. в фильтраторе при изменении много расчетов и иконка моргает
  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    timeoutId.current = window.setTimeout(() => {
      handleSetHasSelected();
    }, 200);

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [checkboxItems, handleSetHasSelected]);

  // Ссылка для применения выбранных фильтров
  const applyUrl = useCallback(
    (url) => {
      const chunks = pathname.split('/');
      // Генерируем ссылку без номера текущей страницы, так как после фильтрации
      // пользователь должен попадать на первую страницу
      const pathnameWithoutPage = chunks
        .filter((chunk) => {
          const isPage = chunk.match(/page-[0-9]{1,}/);

          return !isPage;
        })
        .join('/');

      return `${pathnameWithoutPage}${url}`;
    },
    [pathname],
  );

  const handleToggleCheckbox = useCallback(
    (e: FormEvent, item: CheckboxItemData) => {
      const opts = { id: item.data.parameterId, value: item.data.value[0] };

      if (item.checked) {
        Filtrator.removeCheckbox('catalog', opts);
      } else {
        Filtrator.addCheckbox('catalog', opts);
      }

      const url = Filtrator.toUrl('catalog');

      replaceUrl(applyUrl(url));
    },
    [applyUrl, replaceUrl],
  );

  const handleReset = useCallback(() => {
    Filtrator.resetAll('catalog');
    const url = Filtrator.toUrl('catalog');
    replaceUrl(applyUrl(url));
  }, [applyUrl, replaceUrl]);

  const isDown = useRef<boolean>(false);

  const handleScroll = (event) => {
    if (groupRef.current) {
      groupRef.current.scrollLeft += event.deltaY;
    }
  };

  const handleMouseDown = () => {
    if (groupRef.current) {
      isDown.current = true;
    }
  };

  const handleMouseUp = () => {
    if (isDown.current) {
      isDown.current = false;
    }
  };

  const handlePreventWindowScroll = (event) => {
    const isScrollable = Boolean(groupRef.current.scrollWidth > groupRef.current.clientWidth);

    if (
      (event.target === groupRef.current || groupRef.current?.contains(event.target)) &&
      isScrollable
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = (event) => {
    if (isDown.current && groupRef.current) {
      event.preventDefault();
      event.stopPropagation();
      groupRef.current.scrollLeft += -event.movementX * 2;
    }
  };

  useEffect(() => {
    document.addEventListener('wheel', handlePreventWindowScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', handlePreventWindowScroll);
    };
  }, []);

  return (
    <CheckGroup
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseUp}
      onWheel={handleScroll}
      ref={groupRef}
      className={styles.fastFilters}
      items={checkboxItems}
      handleReset={hasSelected && handleReset}
      handleChange={handleToggleCheckbox}
    />
  );
};

export default TypesFilter;
